import { type H3Event, getCookie, getQuery, getRequestHeader } from 'h3'

import type { Bcp47Locale } from '@backmarket/http-api/src/standards/Locale'
import { getRequestMarket } from '@backmarket/nuxt-module-marketplace/getRequestMarket'
import { toBcp47Locale } from '@backmarket/utils/string/toBcp47Locale'

import { COOKIE_LANGUAGE } from '../constants'

import { getLocaleFromPath } from './getLocaleFromPath'

/**
 * Extracts the locale from a request.
 *
 * Priority:
 * 1. Path (if present and valid)
 *    - Special handling for Nuxt error pages (/__nuxt_error)
 *      Nuxt internally redirects to /__nuxt_error when a route is missing or an error occurs.
 *      The original requested URL is stored in the `url` query param.
 *      We read that to extract the locale, otherwise event.path would only show /__nuxt_error.
 * 2. Cookie (if present)
 * 3. Market default
 */
export function getRequestLocale(event: H3Event): Bcp47Locale {
  const market = getRequestMarket(event)
  const query = getQuery(event)

  if (event.path?.startsWith('/__nuxt_error') && query.error === 'true') {
    const originalUrl = query.url?.toString()
    if (originalUrl) {
      const localeFromPath = getLocaleFromPath(originalUrl) as Bcp47Locale
      const supportedLocales = market.locales?.map(toBcp47Locale) ?? []

      if (localeFromPath && supportedLocales.includes(localeFromPath)) {
        return localeFromPath
      }
    }
  }

  // front-office: extract locale from path
  const localeFromPath = getLocaleFromPath(event.path)
  if (localeFromPath) return toBcp47Locale(localeFromPath)

  // back-offices: extract locale from cookie
  const cookieLocale = getCookie(event, COOKIE_LANGUAGE)
  if (cookieLocale) return toBcp47Locale(cookieLocale)

  // BFF: extract locale from Accept-Language header
  const acceptLanguageHeader = getRequestHeader(event, 'Accept-Language')

  if (acceptLanguageHeader) {
    const validLocales = getRequestMarket(event).locales.map(toBcp47Locale)
    const isValidAcceptLanguage = validLocales.includes(
      acceptLanguageHeader as Bcp47Locale,
    )

    if (isValidAcceptLanguage) return acceptLanguageHeader as Bcp47Locale
  }

  // Fallback: use market default locale
  return toBcp47Locale(market.defaultLocale)
}
